import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Upload, Loader2 } from "lucide-react";
import { PageHero } from "../components/PageHero";
import { Reveal } from "../components/Reveal";
import { PRODUCTS, IMAGES } from "../data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls = "w-full border border-slate-300 bg-white px-4 py-3 text-sm text-brand-charcoal outline-none transition-colors focus:border-brand-red placeholder:text-slate-400";

const Field = ({ label, children }) => (
    <label className="block">
        <span className="mb-1.5 block font-mono text-[10px] tracking-[0.18em] text-slate-500 uppercase">{label}</span>
        {children}
    </label>
);

const Quote = () => {
    const [params] = useSearchParams();
    const preselected = PRODUCTS.find((p) => p.slug === params.get("product"))?.name || "";
    const [form, setForm] = useState({
        company: "", contact_person: "", email: "", phone: "", country: "India",
        industry: "", product: preselected, furnace_type: "", application: "",
        temperature: "", chamber_size: "", power: "", quantity: "1",
        delivery_location: "", delivery_date: "", requirements: "",
    });
    const [file, setFile] = useState(null);
    const [status, setStatus] = useState("idle");

    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        setStatus("sending");
        try {
            const fd = new FormData();
            Object.entries(form).forEach(([k, v]) => fd.append(k, v));
            if (file) fd.append("attachment", file);
            await axios.post(`${API}/quotes`, fd);
            setStatus("sent");
            window.scrollTo(0, 0);
        } catch {
            setStatus("error");
        }
    };

    return (
        <div data-testid="quote-page">
            <PageHero
                kicker="REQUEST A QUOTE"
                title="Request Technical &"
                accent="Commercial Offer"
                sub="Share your process details — our engineering team will review and respond with a technical and commercial proposal."
                image={IMAGES.chamber}
                crumb="Get a Quote"
            />
            <section className="bg-brand-mist py-20 md:py-24 technical-grid-light">
                <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-10">
                    <Reveal>
                        <div className="bg-white p-8 shadow-[0_2px_24px_rgba(15,18,22,0.06)] md:p-12">
                            {status === "sent" ? (
                                <div className="flex min-h-[380px] flex-col items-center justify-center text-center" data-testid="quote-success">
                                    <CheckCircle2 size={52} strokeWidth={1.2} className="text-brand-red" />
                                    <h2 className="mt-6 font-display text-2xl font-extrabold uppercase text-brand-charcoal">Enquiry Received</h2>
                                    <p className="mt-3 max-w-md text-sm leading-relaxed text-slate-500">
                                        Thank you. Your enquiry has been received. Our team will review your requirements and contact you.
                                    </p>
                                </div>
                            ) : (
                                <form onSubmit={submit} data-testid="quote-form">
                                    <div className="grid gap-5 sm:grid-cols-2">
                                        <Field label="Company Name *"><input required data-testid="quote-company" value={form.company} onChange={set("company")} className={inputCls} /></Field>
                                        <Field label="Contact Person *"><input required data-testid="quote-contact-person" value={form.contact_person} onChange={set("contact_person")} className={inputCls} /></Field>
                                        <Field label="Email *"><input required type="email" data-testid="quote-email" value={form.email} onChange={set("email")} className={inputCls} /></Field>
                                        <Field label="Phone *"><input required data-testid="quote-phone" value={form.phone} onChange={set("phone")} className={inputCls} /></Field>
                                        <Field label="Country"><input data-testid="quote-country" value={form.country} onChange={set("country")} className={inputCls} /></Field>
                                        <Field label="Industry"><input data-testid="quote-industry" value={form.industry} onChange={set("industry")} className={inputCls} placeholder="e.g. Tool & Die" /></Field>
                                        <Field label="Product Required">
                                            <select data-testid="quote-product" value={form.product} onChange={set("product")} className={inputCls}>
                                                <option value="">Select a product</option>
                                                {PRODUCTS.map((p) => <option key={p.slug} value={p.name}>{p.name}</option>)}
                                                <option value="Other">Other / Custom</option>
                                            </select>
                                        </Field>
                                        <Field label="Furnace Type"><input data-testid="quote-furnace-type" value={form.furnace_type} onChange={set("furnace_type")} className={inputCls} placeholder="e.g. Chamber / Continuous" /></Field>
                                        <Field label="Application"><input data-testid="quote-application" value={form.application} onChange={set("application")} className={inputCls} placeholder="e.g. Tempering of dies" /></Field>
                                        <Field label="Required Temperature"><input data-testid="quote-temperature" value={form.temperature} onChange={set("temperature")} className={inputCls} placeholder="e.g. 1100°C" /></Field>
                                        <Field label="Working Chamber Size"><input data-testid="quote-chamber-size" value={form.chamber_size} onChange={set("chamber_size")} className={inputCls} placeholder="e.g. 600 × 600 × 900 mm" /></Field>
                                        <Field label="Power Requirement"><input data-testid="quote-power" value={form.power} onChange={set("power")} className={inputCls} placeholder="e.g. 415V, 3-phase" /></Field>
                                        <Field label="Quantity"><input data-testid="quote-quantity" value={form.quantity} onChange={set("quantity")} className={inputCls} /></Field>
                                        <Field label="Delivery Location"><input data-testid="quote-delivery-location" value={form.delivery_location} onChange={set("delivery_location")} className={inputCls} /></Field>
                                        <Field label="Required Delivery Date"><input type="date" data-testid="quote-delivery-date" value={form.delivery_date} onChange={set("delivery_date")} className={inputCls} /></Field>
                                        <div className="sm:col-span-2">
                                            <Field label="Additional Requirements"><textarea rows={4} data-testid="quote-requirements" value={form.requirements} onChange={set("requirements")} className={inputCls} placeholder="Process details, cycle times, loading, atmosphere, automation needs..." /></Field>
                                        </div>
                                        <div className="sm:col-span-2">
                                            <span className="mb-1.5 block font-mono text-[10px] tracking-[0.18em] text-slate-500 uppercase">Upload Drawing / RFQ</span>
                                            <label className="flex cursor-pointer items-center gap-3 border border-dashed border-slate-300 px-4 py-3.5 text-sm text-slate-500 transition-colors hover:border-brand-red" data-testid="quote-upload">
                                                <Upload size={16} className="text-brand-red" />
                                                {file ? file.name : "Attach drawing or RFQ document"}
                                                <input type="file" className="hidden" onChange={(e) => setFile(e.target.files[0] || null)} />
                                            </label>
                                        </div>
                                    </div>
                                    {status === "error" && (
                                        <p className="mt-4 text-sm text-brand-red" data-testid="quote-error">Submission failed. Please try again or contact us directly by phone.</p>
                                    )}
                                    <button type="submit" disabled={status === "sending"} data-testid="quote-submit"
                                        className="mt-8 flex w-full items-center justify-center gap-3 bg-brand-red px-8 py-4 font-display text-sm font-bold tracking-[0.14em] text-white transition-colors hover:bg-brand-charcoal disabled:opacity-60">
                                        {status === "sending" ? <Loader2 size={16} className="animate-spin" /> : null}
                                        REQUEST TECHNICAL & COMMERCIAL OFFER
                                    </button>
                                    <p className="mt-4 text-center font-mono text-[10px] tracking-[0.14em] text-slate-400 uppercase">
                                        Your information is kept confidential and used only to respond to this enquiry.
                                    </p>
                                </form>
                            )}
                        </div>
                    </Reveal>
                </div>
            </section>
        </div>
    );
};

export default Quote;
